import {
  Badge,
  Box,
  Code,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import type { TFunction } from 'i18next'
import type { OrderLifecyclePayment } from '@/types'
import { CopyIconButton, ExplorerButton } from './orderLifecycleShared'

interface OrderPaymentsSectionProps {
  payments?: OrderLifecyclePayment[]
  paymentAddress?: string | null
  explorerChainId?: string | number | null
  displayChainLabel?: string | number | null
  chainCompatibility?: string | null
  t: TFunction
  formatAmount: (value?: string | null) => string
  formatTimestamp: (value?: string | null, locale?: string) => string | null
  truncateMiddle: (value?: string | null, length?: number) => string
  locale?: string
}

export const OrderPaymentsSection = ({
  payments,
  paymentAddress,
  explorerChainId,
  displayChainLabel,
  chainCompatibility,
  t,
  formatAmount,
  formatTimestamp,
  truncateMiddle,
  locale,
}: OrderPaymentsSectionProps) => {
  if (!payments || payments.length === 0) {
    return <Text fontSize="sm">{t('orders.lifecycle.noData')}</Text>
  }

  const txLabel = t('orders.lifecycle.txHash')
  const isSolana = chainCompatibility?.toLowerCase() === 'solana'
  const contractLabel = t('orders.lifecycle.contract', 'Contract Address')
  const paymentAddressLabel = t('orders.lifecycle.paymentAddress', 'Payment Address')
  const vaultTokenAccountLabel = t(
    'orders.lifecycle.vaultTokenAccount',
    'Vault Token Account'
  )
  const blockLabel = t('orders.lifecycle.blockOrSlot', 'Block / Slot')

  return (
    <Box overflowX="auto">
      <Table size="sm" variant="striped">
        <Thead>
          <Tr>
            <Th>{txLabel}</Th>
            <Th>{t('orders.lifecycle.from')}</Th>
            {isSolana ? (
              <>
                <Th>{paymentAddressLabel}</Th>
                <Th>{vaultTokenAccountLabel}</Th>
              </>
            ) : (
              <Th>{contractLabel}</Th>
            )}
            <Th>{t('orders.lifecycle.chain')}</Th>
            <Th>{t('orders.lifecycle.amountUsd')}</Th>
            <Th>{t('orders.lifecycle.status')}</Th>
            <Th>{t('orders.lifecycle.confirmedAt')}</Th>
            <Th>{blockLabel}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {payments.map(payment => (
            <Tr key={payment.id}>
              <Td>
                <HStack spacing={1}>
                  <Code fontSize="xs">{truncateMiddle(payment.txHash)}</Code>
                  <CopyIconButton value={payment.txHash} label={t('common.copy')} />
                  <ExplorerButton type="tx" value={payment.txHash} chainId={explorerChainId} />
                </HStack>
              </Td>
              <Td>
                {payment.from ? (
                  <HStack spacing={1}>
                    <Code fontSize="xs">{truncateMiddle(payment.from)}</Code>
                    <CopyIconButton value={payment.from} label={t('common.copy')} />
                    <ExplorerButton type="address" value={payment.from} chainId={explorerChainId} />
                  </HStack>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    {t('common.na')}
                  </Text>
                )}
              </Td>
              {isSolana && (
                <Td>
                  {paymentAddress ? (
                    <HStack spacing={1}>
                      <Code fontSize="xs">{truncateMiddle(paymentAddress)}</Code>
                      <CopyIconButton value={paymentAddress} label={t('common.copy')} />
                      <ExplorerButton
                        type="address"
                        value={paymentAddress}
                        chainId={explorerChainId}
                      />
                    </HStack>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      {t('common.na')}
                    </Text>
                  )}
                </Td>
              )}
              <Td>
                {payment.contractAddress ? (
                  <HStack spacing={1}>
                    <Code fontSize="xs">{truncateMiddle(payment.contractAddress)}</Code>
                    <CopyIconButton value={payment.contractAddress} label={t('common.copy')} />
                    <ExplorerButton type="address" value={payment.contractAddress} chainId={explorerChainId} />
                  </HStack>
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    {t('common.na')}
                  </Text>
                )}
              </Td>
              <Td>{displayChainLabel ? String(displayChainLabel) : '-'}</Td>
              <Td>{formatAmount(payment.amountUsd)}</Td>
              <Td>
                <Badge colorScheme={payment.status?.confirmed ? 'green' : 'orange'}>
                  {payment.status?.confirmed
                    ? t('orders.paymentConfirmation.confirmed')
                    : t('orders.paymentConfirmation.pending')}
                </Badge>
              </Td>
              <Td>{formatTimestamp(payment.status?.confirmedAt, locale) ?? t('common.na')}</Td>
              <Td>
                {payment.block?.number ?? '-'}
                {payment.block?.logIndex !== undefined && payment.block?.logIndex !== null && (
                  <Text fontSize="xs" color="gray.500">
                    #{payment.block.logIndex}
                  </Text>
                )}
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
    </Box>
  )
}
